import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Trophy, Share2, ArrowRight, RotateCcw, Clock, Target, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import PageFrame from '../components/PageFrame';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SafetyQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  useEffect(() => {
    if (quizId) {
      fetchQuiz(quizId);
    } else {
      fetchQuizList();
    }
  }, [quizId]);

  const fetchQuizList = async () => {
    try {
      const response = await axios.get(`${API}/api/quiz/list`);
      setQuizzes(response.data.quizzes || []);
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuiz = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/quiz/${id}`);
      setQuiz(response.data.quiz);
      setAnswers({});
      setShowResults(false);
      setResults(null);
      setCurrentQuestion(0);
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
      navigate('/quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowNamePrompt(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const submission = {
        quiz_id: quiz.id,
        answers: Object.entries(answers).map(([question_id, selected_answer]) => ({
          question_id,
          selected_answer
        })),
        user_name: userName || undefined,
        user_email: userEmail || undefined
      };

      const response = await axios.post(`${API}/api/quiz/submit`, submission);
      setResults(response.data.result);
      setShowResults(true);
      setShowNamePrompt(false);
    } catch (error) {
      console.error('Failed to submit quiz:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async () => {
    const shareText = results.share_message;
    const shareUrl = window.location.origin + '/quiz';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Truck Safety Quiz Results',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      alert('Results copied to clipboard!');
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setShowResults(false);
    setResults(null);
    setCurrentQuestion(0);
    setUserName('');
    setUserEmail('');
  };

  // Quiz List View
  if (!quizId) {
    return (
      <PageFrame>
        <div className="min-h-screen bg-gray-950 py-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="bg-orange-500/20 text-orange-400 mb-4">Interactive Learning</Badge>
              <h1 className="text-4xl font-bold text-white mb-4">Safety Knowledge Quizzes</h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Test your trucking safety knowledge with quizzes based on real FMCSA and NHTSA data. 
                Challenge yourself and share your results!
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="grid gap-6">
                {quizzes.map((q) => (
                  <Card 
                    key={q.id} 
                    className="bg-gray-900 border-gray-800 hover:border-orange-500/50 transition-all cursor-pointer group"
                    onClick={() => navigate(`/quiz/${q.id}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <Badge className="bg-orange-500/20 text-orange-400 mb-2">{q.category}</Badge>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">
                            {q.title}
                          </h3>
                          <p className="text-gray-400 mb-4">{q.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Target className="h-4 w-4" />
                              {q.question_count} Questions
                            </span>
                            <span className="flex items-center gap-1">
                              <Trophy className="h-4 w-4" />
                              {q.passing_score}% to Pass
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              ~{Math.ceil(q.question_count * 0.5)} min
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="h-8 w-8 text-gray-600 group-hover:text-orange-500 transition-colors" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </PageFrame>
    );
  }

  // Loading state
  if (loading) {
    return (
      <PageFrame>
        <div className="min-h-screen bg-gray-950 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </PageFrame>
    );
  }

  // Results View
  if (showResults && results) {
    return (
      <PageFrame>
        <div className="min-h-screen bg-gray-950 py-12">
          <div className="max-w-3xl mx-auto px-4">
            <Card className="bg-gray-900 border-gray-800 mb-8">
              <CardContent className="p-8 text-center">
                <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
                  results.passed ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {results.passed ? (
                    <Trophy className="h-12 w-12 text-green-500" />
                  ) : (
                    <Target className="h-12 w-12 text-red-500" />
                  )}
                </div>
                
                <h2 className="text-3xl font-bold text-white mb-2">
                  {results.passed ? 'Congratulations!' : 'Keep Learning!'}
                </h2>
                
                <p className="text-5xl font-bold text-orange-500 mb-4">{results.score}%</p>
                
                <p className="text-gray-400 mb-6">
                  You got {results.correct_count} out of {results.total_questions} questions correct.
                  {results.passed 
                    ? ' You passed the quiz!' 
                    : ` You need ${results.passing_score}% to pass.`}
                </p>
                
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button 
                    onClick={handleShare}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Results
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleRetry}
                    className="border-gray-700 text-gray-300"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/quiz')}
                    className="border-gray-700 text-gray-300"
                  >
                    More Quizzes
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Results */}
            <h3 className="text-xl font-bold text-white mb-4">Review Your Answers</h3>
            <div className="space-y-4">
              {results.results.map((r, index) => (
                <Card 
                  key={r.question_id} 
                  className={`border ${r.is_correct ? 'bg-green-900/10 border-green-800' : 'bg-red-900/10 border-red-800'}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      {r.is_correct ? (
                        <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium mb-3">
                          {index + 1}. {r.question}
                        </p>
                        <div className="space-y-2 mb-4">
                          {r.options.map((option, optIndex) => (
                            <div 
                              key={optIndex}
                              className={`p-2 rounded text-sm ${
                                optIndex === r.correct 
                                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                  : optIndex === r.selected && !r.is_correct
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                    : 'text-gray-500'
                              }`}
                            >
                              {option}
                              {optIndex === r.correct && ' ✓'}
                              {optIndex === r.selected && optIndex !== r.correct && ' (Your answer)'}
                            </div>
                          ))}
                        </div>
                        <div className="bg-gray-800/50 p-3 rounded text-sm text-gray-300">
                          <strong className="text-orange-400">Explanation:</strong> {r.explanation}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </PageFrame>
    );
  }

  // Name/Email Prompt
  if (showNamePrompt) {
    return (
      <PageFrame>
        <div className="min-h-screen bg-gray-950 py-12">
          <div className="max-w-md mx-auto px-4">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-center">Almost Done!</CardTitle>
                <CardDescription className="text-center">
                  Enter your details to save your results (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Your Name</label>
                  <Input
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your name"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Email (for safety tips)</label>
                  <Input
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowNamePrompt(false)}
                    className="flex-1 border-gray-700 text-gray-300"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                  >
                    {submitting ? 'Submitting...' : 'See Results'}
                  </Button>
                </div>
                <button
                  onClick={handleSubmit}
                  className="w-full text-sm text-gray-500 hover:text-gray-400"
                >
                  Skip and see results →
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageFrame>
    );
  }

  // Quiz Questions View - Add null check for quiz
  if (!quiz || !quiz.questions) {
    return (
      <PageFrame>
        <div className="min-h-screen bg-gray-950 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </PageFrame>
    );
  }

  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const isAnswered = answers[question.id] !== undefined;

  return (
    <PageFrame>
      <div className="min-h-screen bg-gray-950 py-12">
        <div className="max-w-3xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <Badge className="bg-orange-500/20 text-orange-400 mb-2">{quiz.category}</Badge>
            <h1 className="text-2xl font-bold text-white mb-4">{quiz.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
              <span>Question {currentQuestion + 1} of {quiz.questions.length}</span>
              <span>•</span>
              <span>{quiz.passing_score}% to pass</span>
            </div>
            <Progress value={progress} className="h-2 bg-gray-800" />
          </div>

          {/* Question Card */}
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardContent className="p-8">
              <h2 className="text-xl font-medium text-white mb-6">
                {question.question}
              </h2>
              
              <div className="space-y-3">
                {question.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(question.id, index)}
                    className={`w-full p-4 rounded-lg text-left transition-all ${
                      answers[question.id] === index
                        ? 'bg-orange-500 text-white border-2 border-orange-400'
                        : 'bg-gray-800 text-gray-300 border-2 border-gray-700 hover:border-orange-500/50'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                        answers[question.id] === index
                          ? 'bg-white text-orange-500'
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      {option}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="border-gray-700 text-gray-300"
            >
              Previous
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isAnswered}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {currentQuestion === quiz.questions.length - 1 ? 'Finish Quiz' : 'Next'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Question Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {quiz.questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentQuestion
                    ? 'bg-orange-500 w-6'
                    : answers[q.id] !== undefined
                      ? 'bg-green-500'
                      : 'bg-gray-700'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </PageFrame>
  );
};

export default SafetyQuiz;
